import { BillableSection } from "app/containers/Features/components/TimeTracker/BillableSection";
import RunningTimer from "app/containers/Features/components/TimeTracker/RunningTimer";
import TimeInputField from "app/containers/Features/components/TimeTracker/TimeInputField";
import UserAvatar from "app/containers/Features/components/TimeTracker/UserAvatar";
import { ticketDetailsLocales } from "app/containers/Features/containers/Ticket/locales";
import { timeSheetActions } from "app/containers/Features/containers/TimeSheet/redux/slice";
import {
  ISelectedTicketDetails,
  ITimeTrackerForm,
} from "app/containers/Features/containers/TimeSheet/types";
import { selectUserFormData } from "app/redux/selector";
import React, { FC, useRef } from "react";
import { useSelector } from "react-redux";
import { IEffortActual } from "../../containers/Ticket/types";
import { defaultTimeTrackerForm } from "../../containers/TimeSheet/redux/initialState";
import DateTimePickerRow from "./DateTimePickerRow";
import { useUserTimeInputCardLogic } from "./useUserTimeInputCardLogic";

const UserTimeInputCard: FC<{
  selectedTicketDetail: ISelectedTicketDetails;
  assigneeId?: string;
  callback?: (data: {
    form: ITimeTrackerForm;
    response: IEffortActual;
  }) => void;
  hideTimerStartIcon?: boolean;
}> = ({ selectedTicketDetail, assigneeId, callback, hideTimerStartIcon }) => {
  const {
    formattedTime,
    toggle,
    handleTimerStart,
    isRunning,
    timeTrackerForm,
    activeTimerForm,
    enablePreview,
    effortActualRequestLoading,
    effortInput,
    setEffortInput,
    formattedEffort,
    timerRunning,
    typeData,
    handleSubmit,
    handlePreview,
    dispatch,
  } = useUserTimeInputCardLogic({ selectedTicketDetail, assigneeId, callback });
  const currentUserData = useSelector(selectUserFormData);
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="p-4">
      <UserAvatar
        fullName={currentUserData?.fullName}
        avatar={currentUserData?.avatar}
      />
      <div className="px-2 py-[5px] border border-gray-100 border-t-0">
        {timerRunning && !hideTimerStartIcon ? (
          <RunningTimer
            runningTime={formattedTime}
            onClickTimerIcon={toggle}
            timeRunning={timerRunning}
          />
        ) : (
          <TimeInputField
            hideIcon={hideTimerStartIcon}
            timerRunning={timerRunning}
            onClickTimerIcon={(e) => {
              e.stopPropagation();
              if (isRunning) {
                handleTimerStart({
                  newTask: {
                    _id: selectedTicketDetail?._id,
                    ticketName: selectedTicketDetail?.ticketName,
                    projectId: selectedTicketDetail?.projectId,
                    typeIcon: typeData?.icon,
                  },
                });
              } else {
                toggle();
                dispatch(
                  timeSheetActions.setActiveTimerForm({
                    ...activeTimerForm,
                    selectedTicketDetails: {
                      _id: selectedTicketDetail?._id,
                      ticketName: selectedTicketDetail?.ticketName,
                      projectId: selectedTicketDetail?.projectId,
                      typeIcon: typeData?.icon,
                    },
                    date: new Date().toISOString(),
                    startTime: new Date().toISOString(),
                  }),
                );
              }
            }}
            inputProps={{
              autoFocus: true,
              id: "time-input",
              placeholder: ticketDetailsLocales.timeTrackerInputPlaceholder,
              value: enablePreview ? formattedEffort : effortInput,
              onChange: (e) => {
                setEffortInput(e.target.value);
                if (!e.target.value.trim()) {
                  dispatch(
                    timeSheetActions.setTimeTrackerForm(defaultTimeTrackerForm),
                  );
                  dispatch(timeSheetActions.setEnableTimeTrackerPreview(false));
                }
              },
              onKeyDown: (e) => {
                if (e.key === "Enter") handlePreview();
              },
              onBlur: handlePreview,
              inputRef: inputRef,
            }}
            enablePreview={enablePreview}
            previewValue={formattedEffort}
            onClickPreview={() => {
              dispatch(timeSheetActions.setEnableTimeTrackerPreview(false));
              setEffortInput(formattedEffort);
              setTimeout(() => {
                inputRef.current?.focus();
                inputRef.current?.select();
              }, 0);
            }}
          />
        )}
      </div>
      <DateTimePickerRow disabled={timerRunning && !hideTimerStartIcon} />
      <BillableSection
        billableSwitchProps={{
          checked: timeTrackerForm.billableHours as boolean,
          onChange: (checked) =>
            dispatch({
              type: "timeSheet/setEffortActualSingleValue",
              payload: { field: "billableHours", value: checked },
            }),
        }}
        saveButtonProps={{
          children: "Save",
          disabled:
            !enablePreview ||
            (timeTrackerForm.hours === 0 && timeTrackerForm.minutes === 0),
          onClick: handleSubmit,
          isLoading: effortActualRequestLoading,
        }}
      />
    </div>
  );
};

export default UserTimeInputCard;
